import React, { lazy, Suspense, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import * as Sentry from '@sentry/react';
import BottomTabBar from './components/clean/BottomTabBar';
// KAN-328: legacy-user prompt for the username system. Shown once per
// session for any authed user whose `username_set_explicitly` is false.
import { UsernamePromptGate } from './components/auth/UsernamePromptModal';
// KAN-name-capture: post-magic-link first/last/display capture. Mounted
// AFTER UsernamePromptGate so the two modals don't stack — the gate skips
// itself while username_set_explicitly=false (handing off to the username
// gate first).
import { NamePromptGate } from './components/auth/NamePromptModal';
// KAN-3057 Phase 3: 7-step driver.js onboarding tour for first-run users.
// Gated by PostHog flag `onboarding_tour_v1` (default OFF). Fires after
// UsernamePromptGate clears. Replay path lives in Profile · Settings.
import { OnboardingTourGate } from './components/onboarding/OnboardingTourGate';
// KAN-21417: first-launch disclaimer gate for Apple Review Guideline 5.3.4.
// Renders a fixed-overlay modal blocking all routes for Capacitor shells +
// mobile-web visitors until the user acknowledges. Desktop browsers no-op
// (their compliance surface is the footer Terms link). Mirrors the iOS
// Expo gate at `apps/ios/components/onboarding/DisclaimerGate.tsx`.
import DisclaimerGate from './components/onboarding/DisclaimerGate';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { isNativePlatform } from './utils/platform';
import { SupabaseAuthProvider, useAuth } from './contexts/SupabaseAuthContext';
import { useMarketingLandingV1 } from './lib/featureFlags';
import { RealTimeProvider } from './contexts/RealTimeProvider';
import { SportProvider } from './contexts/SportContext';
import { SportApiSync } from './components/SportApiSync';
import Loader from './components/Loader';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorFallback from './components/RouteErrorFallback';
import CookieConsentBanner from './components/CookieConsentBanner';

// KAN-374: per-route Sentry ErrorBoundary helper. Each top-level route gets
// its own boundary with a distinct `route` tag so a render-time crash on
// one tab (e.g. Track) doesn't take the rest of the SPA down — the
// BottomTabBar + header nav stay live and the user can switch tabs.
//
// `Sentry.ErrorBoundary` reports the caught error to Sentry automatically
// (no need to call captureException ourselves) and supplies `resetError`
// to the fallback so the user can retry.
const RouteBoundary: React.FC<{ route: string; children: React.ReactNode }> = ({
  route,
  children,
}) => (
  <Sentry.ErrorBoundary
    beforeCapture={(scope) => {
      scope.setTag('route', route);
      scope.setTag('boundary', 'route');
    }}
    fallback={({ error, resetError }) => (
      <RouteErrorFallback route={route} error={error} resetError={resetError} />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

// Lazy load components for better performance
const UltimateCleanHub = lazy(() => import('./components/clean/UltimateCleanHub'));
// KAN-3125 Phase 3: marketing landing reveal at `/` for anon users when the
// `marketing_landing_v1` PostHog flag is on. Lazy-loaded so the bundle cost
// only lands on actual visitors who hit the gate. When the flag is off (the
// default) `/` continues to render UltimateCleanHub.
const MarketingLanding = lazy(() => import('./pages/MarketingLanding'));
// KAN-3113 v3 (2026-05-07): SubscriptionAnalytics stays unmounted (collapsed
// into PaymentMonitoring tab #3 per Figma 52:284). SystemPerformanceDashboard
// is RESTORED with a placeholder shell (Figma 52:522) — per the v3 spawn
// brief, the rule is "render the shell with placeholder copy, NOT a
// full-screen Failed to Load". The shell ships honest "—" values + an
// explicit notice band naming the missing Render endpoint.
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AdminOverview = lazy(() => import('./admin/AdminOverview'));
const UserManagement = lazy(() => import('./admin/UserManagement'));
const PaymentMonitoring = lazy(() => import('./admin/PaymentMonitoring'));
const UserActivityDashboard = lazy(() => import('./admin/UserActivityDashboard'));
const PicksModels = lazy(() => import('./admin/PicksModels'));
const SystemPerformanceDashboard = lazy(() => import('./admin/SystemPerformanceDashboard'));
// KAN-name-capture: per-user promo attribution drill-down (companion to the
// AdminOverview aggregate sparkline). Renders v_promo_attribution_summary.
const PromoAttribution = lazy(() => import('./admin/PromoAttribution'));
const TierUpgrade = lazy(() => import('./pages/TierUpgrade'));
const Profile = lazy(() => import('./pages/Profile'));
const Performance = lazy(() => import('./pages/Performance'));
const Track = lazy(() => import('./pages/Track'));
const CachedPredictions = lazy(() => import('./pages/CachedPredictions'));
const Login = lazy(() => import('./pages/Login'));
const ConnectionTest = lazy(() => import('./components/ConnectionTest'));
const NotFound = lazy(() => import('./pages/NotFound'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const AccessibilityStatement = lazy(() => import('./pages/AccessibilityStatement'));
const Support = lazy(() => import('./pages/Support'));
// Launch Marketing Campaign — public donation rail. Lazy-loaded; placeholder
// content when `VITE_DONATE_PAYMENT_LINK_URL` is unset (see Donate.tsx).
const Donate = lazy(() => import('./pages/Donate'));

// Optimized query client configuration for betting data
// PERFORMANCE OPTIMIZATION: Reduced refetching since Realtime handles instant updates
// Materialized view refreshes every 30s, so we can safely cache longer
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 3, // 3 minutes (increased from 2 - Realtime provides instant updates)
      gcTime: 1000 * 60 * 15, // 15 minutes garbage collection (increased from 10)
      retry: 1,
      refetchOnWindowFocus: false, // Disabled - Realtime keeps data fresh
      refetchOnReconnect: true, // Refetch on network reconnect
    },
  },
});

// TypeScript interfaces for type safety
interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
}

// Protected route wrapper with type safety
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { user, hasSession, isAdmin, loading, profileLoaded } = useAuth();
  const location = useLocation();

  // KAN-45 / KAN-40 / KAN-5912: hold the loader while EITHER:
  //   - the auth context is still in its loading phase, or
  //   - we have a session but no user object yet (fallback hasn't been
  //     applied — sub-millisecond window in practice), or
  //   - this is an admin route AND the profile fetch hasn't resolved yet —
  //     without this, the fallback user with is_admin=false short-circuits
  //     the admin guard and redirects authed admins to / on every direct
  //     URL hit / page refresh / bookmark navigation.
  if (
    loading ||
    (hasSession && !user) ||
    (hasSession && requireAdmin && !profileLoaded)
  ) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size="xl" />
      </div>
    );
  }

  // KAN-24929 / KAN-23816: no session — redirect to /login, preserving where
  // the user wanted to go via TWO channels:
  //   1. ?return_to=<encoded-path> in the URL — survives hard refresh, OAuth
  //      round-trips, email-confirm, bookmarks, and shared links. This is the
  //      structural channel; consumed by Login.tsx via URLSearchParams.
  //   2. location.state.from in-memory router state — survives SPA-internal
  //      navigation only. Retained for back-compat with any caller still
  //      reading state.from (Login.tsx falls through to it if the URL param
  //      is missing or fails the open-redirect guard).
  // Background: KAN-23816 prod sweep found 4 protected routes "showed Login at
  // their URL". The HOC was already using <Navigate> — the actual gap was that
  // state.from was lost across refreshes, OAuth, and external return links.
  if (!hasSession) {
    // Include location.hash so deep links like /track#section-live survive
    // the auth round-trip (Bug Hunter Finding 6).
    const target = location.pathname + location.search + location.hash;
    return (
      <Navigate
        to={`/login?return_to=${encodeURIComponent(target)}`}
        replace
        state={{ from: target }}
      />
    );
  }

  // Session present but no user (profile fetch failed permanently) — render
  // children anyway; the page can handle a missing-tier user. Better than
  // redirecting an authenticated user away from a page they paid to see.
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Admin route guard component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute requireAdmin={true}>
      {children}
    </ProtectedRoute>
  );
};

// KAN-3113 v3: the global Home/Performance/Track/Profile/Admin tab bar is
// the user-app surface. The admin panel renders its own admin-level mobile
// tab bar (Overview/Users/Activity/Picks/More) per Figma 260:2, so /admin*
// paths SHOULD NOT also show the global tab bar — that's why the previous
// pass shipped two stacked tab bars on mobile admin.
const TAB_BAR_PATHS = ['/', '/performance', '/track', '/profile', '/settings', '/predictions', '/upgrade'];

// KAN-3125 Phase 3: branches `/` between the new MarketingLanding (anon +
// `marketing_landing_v1` flag on) and the existing UltimateCleanHub
// (everyone else). Hides the global BottomTabBar when the marketing landing
// is rendered — the marketing surface owns its own header / footer chrome.
const RootRoute: React.FC<{ onMarketingActive: (active: boolean) => void }> = ({
  onMarketingActive,
}) => {
  const { hasSession } = useAuth();
  const flagOn = useMarketingLandingV1();
  const showMarketing = !hasSession && flagOn;
  // Inform the parent so it can suppress the BottomTabBar when the marketing
  // surface is live (the landing has its own footer chrome).
  useEffect(() => {
    onMarketingActive(showMarketing);
    return () => onMarketingActive(false);
  }, [showMarketing, onMarketingActive]);
  return showMarketing ? <MarketingLanding /> : <UltimateCleanHub />;
};

// Separate routes component for better organization
function AppRoutes() {
  const { pathname } = useLocation();
  const isAdmin = pathname === '/admin' || pathname.startsWith('/admin/');
  // KAN-3125: when the marketing landing is active at `/`, suppress the
  // global BottomTabBar — the landing renders its own header + footer.
  const [marketingActive, setMarketingActive] = React.useState(false);
  const showTabBar =
    !isAdmin &&
    !(marketingActive && pathname === '/') &&
    TAB_BAR_PATHS.some(p => (p === '/' ? pathname === '/' : pathname.startsWith(p)));

  return (
    <>
      <div className={showTabBar ? 'pb-[72px] md:pb-0' : ''}>
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader size="xl" /></div>}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<RouteBoundary route="login"><Login /></RouteBoundary>} />
            <Route path="/signin" element={<Navigate to="/login" replace />} />
            {/* KAN-25023: SPA-side aliases for the signup affordances that
                vercel.json already 308s on cold HTTP (/sign-up, /signup,
                /register → /login?mode=signup). The Vercel redirects only
                fire on initial page load; in-app SPA navigation to these
                paths must also land on Login in signup mode. */}
            <Route path="/sign-up" element={<Navigate to="/login?mode=signup" replace />} />
            <Route path="/signup" element={<Navigate to="/login?mode=signup" replace />} />
            <Route path="/register" element={<Navigate to="/login?mode=signup" replace />} />
            {/* KAN-23650: `/auth` is a conventional auth URL; redirect
                rather than 404 so external links don't dead-end. */}
            <Route path="/auth" element={<Navigate to="/login" replace />} />
            {/* KAN-23650: `/marketing` (unprefixed alias for the landing
                surface that lives at `/` behind the marketing_landing_v1
                feature flag) was 404ing; send it to `/` so it always
                resolves to the appropriate hub. */}
            <Route path="/marketing" element={<Navigate to="/" replace />} />
            {/* KAN-25023 (Apple 2.1 risk close-out): three legacy / convention
                URLs that 404'd pre-fix. `/landing` is the old marketing
                URL — alias it to `/`. `/home` and `/onboarding` were
                dead-ends with no Sign-In CTA for anonymous reviewers; map
                both to `/` (which auto-fires the onboarding tour for
                first-run authed users and renders the marketing landing
                surface for anon visitors). The structural auth-guard
                consolidation is tracked separately as Bundle 5 (KAN-24929);
                these direct redirects are the targeted P0 fix. */}
            <Route path="/landing" element={<Navigate to="/" replace />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            {/* KAN-25402 (Lens 2 finding #20): /account/billing + /account/delete
                were conventional shortcut URLs a reviewer might type to
                verify 5.1.1(v) account-deletion presence. Both 404'd. The
                in-app deletion path lives at Profile → Data & Privacy →
                "Delete my account". Send both shortcuts to /profile so an
                anonymous reviewer is bounced through /login first
                (ProtectedRoute on /profile preserves return_to via state),
                and an authed reviewer lands directly on the page that owns
                both billing + delete affordances. /m/account/delete is
                handled by Expo Router separately. */}
            <Route path="/account/billing" element={<Navigate to="/profile" replace />} />
            <Route path="/account/delete" element={<Navigate to="/profile" replace />} />
            <Route path="/onboarding" element={<Navigate to="/" replace />} />
            <Route path="/forgot-password" element={<RouteBoundary route="forgot-password"><ForgotPassword /></RouteBoundary>} />
            <Route path="/reset-password" element={<RouteBoundary route="reset-password"><ResetPassword /></RouteBoundary>} />
            {/* KAN-1932: ConnectionTest is an internal diagnostic tool that surfaces Supabase URL + auto-creates test accounts. Admin-only. */}
            <Route path="/test-connection" element={
              <AdminRoute>
                <RouteBoundary route="test-connection"><ConnectionTest /></RouteBoundary>
              </AdminRoute>
            } />

            {/* Legal & Support pages */}
            <Route path="/terms" element={<RouteBoundary route="terms"><TermsOfService /></RouteBoundary>} />
            <Route path="/privacy" element={<RouteBoundary route="privacy"><PrivacyPolicy /></RouteBoundary>} />
            <Route path="/accessibility" element={<RouteBoundary route="accessibility"><AccessibilityStatement /></RouteBoundary>} />
            {/* KAN-25404 (Apple 2.1 + Lens 3 LOSE #5): `/legal/*` slug
                aliases. Apple App Store metadata, external links, and PR
                review-room links commonly use `/legal/terms` and
                `/legal/privacy` conventions. Pre-fix these dropped to the
                catch-all NotFound (200 OK to curl + index.html, but React
                rendered NotFound). Aliasing to the canonical `/terms`,
                `/privacy`, `/accessibility` keeps a single source of truth
                for the actual content while eliminating the dead-end. */}
            <Route path="/legal" element={<Navigate to="/terms" replace />} />
            <Route path="/legal/terms" element={<Navigate to="/terms" replace />} />
            <Route path="/legal/privacy" element={<Navigate to="/privacy" replace />} />
            <Route path="/legal/accessibility" element={<Navigate to="/accessibility" replace />} />

            {/* Launch Marketing Campaign — public donation rail (anon OK). */}
            <Route path="/donate" element={<RouteBoundary route="donate"><Donate /></RouteBoundary>} />
            {/* KAN-10892: real customer-support page (Apple App Store submission requires a non-redirect Support URL). */}
            <Route path="/support" element={<RouteBoundary route="support"><Support /></RouteBoundary>} />

            {/* Protected user routes */}
            {/* Phase D: /settings folded into /profile (Profile owns the Settings tab). */}
            <Route path="/settings" element={<Navigate to="/profile" replace />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <RouteBoundary route="profile"><Profile /></RouteBoundary>
              </ProtectedRoute>
            } />
            <Route path="/performance" element={<RouteBoundary route="performance"><Performance /></RouteBoundary>} />
            <Route path="/track" element={
              <ProtectedRoute>
                <RouteBoundary route="track"><Track /></RouteBoundary>
              </ProtectedRoute>
            } />
            <Route path="/predictions/upcoming" element={
              <ProtectedRoute>
                <RouteBoundary route="predictions-upcoming"><CachedPredictions /></RouteBoundary>
              </ProtectedRoute>
            } />
            {/* KAN-25551 — /upgrade must be a public route so anonymous
                promo-link clicks (e.g. /upgrade?promo=FRIENDS-LAUNCH-2026)
                render the claim panel instead of bouncing through /login.
                TierUpgrade itself uses `useAuth().user` to switch between
                the anonymous claim panel, the FREE-tier pricing grid, and
                the DEGENERATE referral card. There are no protected
                actions inside TierUpgrade — the Subscribe handler already
                checks `!user` and routes to signup, and the auto-redeem
                effect requires `user` to be non-null before firing. */}
            <Route path="/upgrade" element={
              <RouteBoundary route="upgrade"><TierUpgrade /></RouteBoundary>
            } />
            <Route path="/tiers" element={<Navigate to="/upgrade" replace />} />
            <Route path="/pricing" element={<Navigate to="/upgrade" replace />} />

            {/* Payment callback routes */}
            <Route path="/payment-success" element={
              <ProtectedRoute>
                <RouteBoundary route="payment-success"><PaymentSuccess /></RouteBoundary>
              </ProtectedRoute>
            } />
            <Route path="/payment-cancel" element={<RouteBoundary route="payment-cancel"><PaymentCancel /></RouteBoundary>} />

            {/* Admin-only routes */}
            <Route path="/admin" element={
              <AdminRoute>
                <RouteBoundary route="admin"><AdminLayout /></RouteBoundary>
              </AdminRoute>
            }>
              <Route index element={<AdminOverview />} />
              <Route path="dashboard" element={<Navigate to="/admin" replace />} />
              <Route path="users" element={<UserManagement />} />
              {/* KAN-3113 remediation: subscriptions live as the 3rd tab of
                  PaymentMonitoring per Figma 52:284. Bookmarks redirect. */}
              <Route path="subscriptions" element={<Navigate to="/admin/payments?tab=subscriptions" replace />} />
              <Route path="payments" element={<PaymentMonitoring />} />
              <Route path="activity" element={<UserActivityDashboard />} />
              <Route path="picks" element={<PicksModels />} />
              {/* KAN-name-capture: promo-code attribution drill-down. */}
              <Route path="promo" element={<PromoAttribution />} />
              {/* KAN-3113 v3: System page now renders the placeholder shell
                  (no "Failed to Load" surface). Real values land when the
                  Render `/api/admin/system-performance` endpoint ships. */}
              <Route path="system" element={<SystemPerformanceDashboard />} />
            </Route>

            {/* Main application (public). KAN-3125: branches between
                MarketingLanding (anon + `marketing_landing_v1` on) and
                UltimateCleanHub (all other cases). */}
            <Route
              path="/"
              element={
                <RouteBoundary route="home">
                  <RootRoute onMarketingActive={setMarketingActive} />
                </RouteBoundary>
              }
            />

            {/* 404 catch-all - must be last */}
            <Route path="*" element={<RouteBoundary route="not-found"><NotFound /></RouteBoundary>} />
          </Routes>
        </Suspense>
      </div>
      {showTabBar && <BottomTabBar />}
      {/* KAN-328: prompt legacy users to confirm/set their username. Renders
          nothing unless the authed user has `username_set_explicitly === false`
          and hasn't deferred this session. Mounted globally so it covers every
          authed page, not just /profile. */}
      <UsernamePromptGate />
      {/* KAN-name-capture: post-magic-link first/last/display capture. Renders
          nothing when the user's first_name is already populated OR when the
          UsernamePromptGate is still active (single-modal-at-a-time discipline).
          Stripe webhook lazily fills first/last from billing details on first
          checkout — this gate is the explicit-typed path. */}
      <NamePromptGate />
      {/* KAN-3057: first-run onboarding tour. Renders nothing unless the
          PostHog flag `onboarding_tour_v1` is on AND the user has all 3 tour
          timestamps NULL AND the username gate has cleared. */}
      <OnboardingTourGate />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SupabaseAuthProvider>
          <SportProvider>
            <SportApiSync />
            <RealTimeProvider>
              <DisclaimerGate>
                <Router>
                    <AppRoutes />
                  </Router>
                {!isNativePlatform() && <Analytics />}
                {!isNativePlatform() && <SpeedInsights />}
                {!isNativePlatform() && <CookieConsentBanner />}
                <Toaster
                  position="bottom-right"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      background: '#1f2937',
                      color: '#f3f4f6',
                      border: '1px solid #374151',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    },
                    success: {
                      iconTheme: {
                        primary: '#10b981',
                        secondary: '#f3f4f6',
                      },
                    },
                    error: {
                      iconTheme: {
                        primary: '#ef4444',
                        secondary: '#f3f4f6',
                      },
                    },
                  }}
                />
              </DisclaimerGate>
            </RealTimeProvider>
          </SportProvider>
        </SupabaseAuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
